import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Settings, 
  User, 
  Lock, 
  Bell, 
  BarChart3,
  Save,
  Eye,
  EyeOff,
  Shield,
  Globe,
  Clock,
  Mail,
  Calendar,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  Code,
  Database,
  Briefcase,
  Key,
  Upload,
  Download,
  Trash2,
  Server
} from 'lucide-react';
import { toast } from 'react-toastify';

import { settingsAPI, adminAPI, authAPI, handleApiError } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import useConfirm from '../../hooks/useConfirm';
import ConfirmationModal from '../../components/modals/ConfirmationModal';
import AutoCleanupSettings from '../../components/settings/AutoCleanupSettings';
import NotificationsSection from './SettingsPageNotifications';
import { getFallbackIndustryOptions } from '../../utils/industries';

const SETTINGS_TOAST_IDS = {
  companyLogoLoadError: 'settings-company-logo-load-error'
};

const SettingsPage = () => {
  const [activeTab, setActiveTab] = useState('account');
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userData, setUserData] = useState({});
  
  // Utiliser le hook useAuth pour pouvoir mettre à jour le contexte global
  const { refreshUser } = useAuth();
  const { t, changeLanguage, currentLanguage } = useLanguage();
  const { confirm, confirmProps } = useConfirm();
  
  // Removed legacy usage hook (pricing disabled)
  
  // Profile form data
  const [profileData, setProfileData] = useState({
    name: '',
    email: ''
  });
  
  // Password form data
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // Preferences form data
  const [preferencesData, setPreferencesData] = useState({
    language: 'fr',
    cleanup_delay: 30
  });

  // Notifications form data (Admin only)
  const [notificationsData, setNotificationsData] = useState({
    // Application notifications
    application_notifications_enabled: true,
    default_notify_publisher: true,
    custom_notification_users: [],
    // Appointment notifications
    appointment_notifications_enabled: true,
    appointment_notify_recruiter: true,
    appointment_custom_users: [],
    // Analysis notifications
    analysis_notifications_enabled: true,
    analysis_notify_executor: true,
    analysis_custom_users: []
  });

  // HR Users list for notifications
  const [hrUsers, setHrUsers] = useState([]);

  // Company settings (Admin only)
  const [companyData, setCompanyData] = useState({
    company_name: '',
    industry: '',
    company_logo: null,
    company_logo_url: ''
  });
  const [industryOptions, setIndustryOptions] = useState(() => getFallbackIndustryOptions((k, f) => f || k));
  const [logoFile, setLogoFile] = useState(null);

  // MinIO custom configuration (Enterprise Admin only)
  const [minioConfig, setMinioConfig] = useState({
    endpoint: '',
    port: '9000',
    useSSL: false,
    accessKey: '',
    secretKey: '',
    publicUrl: ''
  });
  const [minioConfigInfo, setMinioConfigInfo] = useState({
    hasCustomConfig: false,
    isEnabled: false,
    migrationStatus: 'none',
    migrationDirection: null,
    configuredAt: null,
    lastUpdated: null,
    endpoint: null,
    port: null,
    useSSL: false,
    publicUrl: null,
    defaultBucket: null,
    systemBucket: null
  });
  const [testingMinio, setTestingMinio] = useState(false);
  const [savingMinio, setSavingMinio] = useState(false);
  const [showMinioForm, setShowMinioForm] = useState(false);
  const [migrationEstimate, setMigrationEstimate] = useState(null);
  const [showMigrationDetails, setShowMigrationDetails] = useState(false);
  const [currentJobs, setCurrentJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);

  // AI provider configuration (Admin only)
  const [aiProviders, setAiProviders] = useState({
    openai: { configured: false, updatedAt: null, tokenPreview: null },
    anthropic: { configured: false, updatedAt: null, tokenPreview: null }
  });
  const [aiProviderTokens, setAiProviderTokens] = useState({
    openai: '',
    anthropic: ''
  });
  const [aiProviderVisibility, setAiProviderVisibility] = useState({
    openai: false,
    anthropic: false
  });
  const [aiProviderLoading, setAiProviderLoading] = useState(false);
  const [aiProviderSaving, setAiProviderSaving] = useState({
    openai: false,
    anthropic: false
  });

  // Fetch notifications data (Admin only)
  const fetchNotificationsData = useCallback(async () => {
    try {
      const response = await settingsAPI.getNotifications();
      const data = response.data || {};
      setNotificationsData({
        application_notifications_enabled: data.application_notifications_enabled !== false,
        default_notify_publisher: data.default_notify_publisher !== false,
        custom_notification_users: data.custom_notification_users || [],
        appointment_notifications_enabled: data.appointment_notifications_enabled !== false,
        appointment_notify_recruiter: data.appointment_notify_recruiter !== false,
        appointment_custom_users: data.appointment_custom_users || [],
        analysis_notifications_enabled: data.analysis_notifications_enabled !== false,
        analysis_notify_executor: data.analysis_notify_executor !== false,
        analysis_custom_users: data.analysis_custom_users || []
      });
    } catch (error) {
      console.error('Error fetching notifications data:', error);
    }
  }, []);

  // Fetch HR users list (Admin only)
  const fetchHrUsers = useCallback(async () => {
    try {
      const response = await adminAPI.getUsers();
      setHrUsers(response.data?.users || []);
    } catch (error) {
      console.error('Error fetching HR users:', error);
    }
  }, []);

  // Fetch company data (Admin only)
  const fetchCompanyData = useCallback(async () => {
    try {
      const response = await settingsAPI.getCompany();
      const data = response.data || {};
      setCompanyData({
        company_name: data.company_name || '',
        industry: data.industry || '',
        company_logo: data.company_logo || null,
        company_logo_url: data.company_logo_url || ''
      });
    } catch (error) {
      console.error('Error fetching company data:', error);
    }
  }, []);

  const fetchIndustryOptions = useCallback(async () => {
    try {
      const response = await authAPI.getIndustries(currentLanguage);
      const options = response?.data?.options;
      if (Array.isArray(options) && options.length > 0) {
        setIndustryOptions(options);
        return;
      }
    } catch (error) {
      console.warn('Failed to fetch industry options, using fallback list.');
    }
    setIndustryOptions(getFallbackIndustryOptions(t));
  }, [currentLanguage, t]);

  // Handle profile update
  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    if (!profileData.name.trim() || !profileData.email.trim()) {
      toast.error(t('settings.errors.fillAllFields', 'Veuillez remplir tous les champs'));
      return;
    }

    setSaving(true);
    try {
      await settingsAPI.updateProfile(profileData);
      toast.success(t('settings.profile.updateSuccess', 'Profil mis à jour avec succès'));
      
      // Refresh user data in AuthContext to update the Dashboard greeting immediately
      await refreshUser();
      
      // Also refresh local data
      await fetchUserData();
    } catch (error) {
      const errorMessage = handleApiError(error, t('settings.profile.updateError', 'Erreur lors de la mise à jour du profil'));
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  // Handle password change
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      toast.error(t('settings.errors.fillAllFields', 'Veuillez remplir tous les champs'));
      return;
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error(t('settings.password.mismatch', 'Les nouveaux mots de passe ne correspondent pas'));
      return;
    }
    
    if (passwordData.newPassword.length < 8) {
      toast.error(t('settings.password.minLength', 'Le nouveau mot de passe doit contenir au moins 8 caractères'));
      return;
    }

    setSaving(true);
    try {
      await settingsAPI.updateProfile({
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      });
      toast.success(t('settings.password.updateSuccess', 'Mot de passe modifié avec succès'));
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      const errorMessage = handleApiError(error, t('settings.password.updateError', 'Erreur lors du changement de mot de passe'));
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  // Handle preferences update
  const handlePreferencesUpdate = async (e) => {
    e.preventDefault();
    
    setSaving(true);
    try {
      await settingsAPI.updatePreferences(preferencesData);
      // Update the language context as well
      await changeLanguage(preferencesData.language);
      toast.success(t('settings.preferences.updateSuccess', 'Préférences mises à jour avec succès'));
      await fetchUserData(); // Refresh data
    } catch (error) {
      const errorMessage = handleApiError(error, t('settings.preferences.updateError', 'Erreur lors de la mise à jour des préférences'));
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  // Handle notifications update (Admin only)
  const handleNotificationsUpdate = async (e) => {
    e.preventDefault();
    
    setSaving(true);
    try {
      await settingsAPI.updateNotifications(notificationsData);
      toast.success(t('settings.notifications.updateSuccess', 'Paramètres de notifications mis à jour avec succès'));
      await fetchNotificationsData();
    } catch (error) {
      console.error('Error updating notifications:', error);
      toast.error(t('settings.notifications.updateError', 'Erreur lors de la mise à jour des notifications'));
    } finally {
      setSaving(false);
    }
  };

  // Toggle user in custom notification list for applications
  const toggleUserNotification = (userId) => {
    setNotificationsData(prev => ({
      ...prev,
      custom_notification_users: prev.custom_notification_users.includes(userId)
        ? prev.custom_notification_users.filter(id => id !== userId)
        : [...prev.custom_notification_users, userId]
    }));
  };

  // Toggle user in custom notification list for appointments
  const toggleAppointmentUser = (userId) => {
    setNotificationsData(prev => ({
      ...prev,
      appointment_custom_users: prev.appointment_custom_users.includes(userId)
        ? prev.appointment_custom_users.filter(id => id !== userId)
        : [...prev.appointment_custom_users, userId]
    }));
  };

  // Toggle user in custom notification list for analyses
  const toggleAnalysisUser = (userId) => {
    setNotificationsData(prev => ({
      ...prev,
      analysis_custom_users: prev.analysis_custom_users.includes(userId)
        ? prev.analysis_custom_users.filter(id => id !== userId)
        : [...prev.analysis_custom_users, userId]
    }));
  };

  // Handle company update (Admin only)
  const handleCompanyUpdate = async (e) => {
    e.preventDefault();
    
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('company_name', companyData.company_name);
      formData.append('industry', companyData.industry || '');
      
      if (logoFile) {
        formData.append('company_logo', logoFile);
      }

      await settingsAPI.updateCompany(formData);
      toast.success(t('settings.company.updateSuccess', 'Paramètres entreprise mis à jour avec succès'));
      await fetchCompanyData();
      setLogoFile(null);
    } catch (error) {
      console.error('Error updating company settings:', error);
      toast.error(t('settings.company.updateError', 'Erreur lors de la mise à jour des paramètres entreprise'));
    } finally {
      setSaving(false);
    }
  };

  // Handle logo file selection
  const handleLogoFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast.error(t('settings.company.logo.invalidFormat', 'Format de fichier non supporté. Utilisez JPG, PNG, GIF ou WebP.'));
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error(t('settings.company.logo.tooLarge', 'Le fichier est trop volumineux. Taille maximum : 5MB.'));
        return;
      }
      
      setLogoFile(file);
      
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setCompanyData(prev => ({ ...prev, company_logo_url: previewUrl }));
    }
  };

  // Remove logo
  const handleRemoveLogo = () => {
    setLogoFile(null);
    setCompanyData(prev => ({ ...prev, company_logo_url: '', company_logo: null }));
  };

  const handleCompanyLogoLoadError = useCallback(() => {
    if (toast.isActive(SETTINGS_TOAST_IDS.companyLogoLoadError)) return;
    toast.error(
      t('settings.company.logo.loadError', 'Impossible de charger le logo. Réessayez avec une image valide.'),
      { toastId: SETTINGS_TOAST_IDS.companyLogoLoadError }
    );
    setCompanyData(prev => ({ ...prev, company_logo_url: '' }));
  }, [t]);

  // Fetch MinIO configuration info
  const fetchMinioConfigInfo = useCallback(async () => {
    try {
      const response = await settingsAPI.getMinioConfig();
      setMinioConfigInfo(response.data || {});
    } catch (error) {
      console.error('Error fetching MinIO config info:', error);
    }
  }, []);

  // Test MinIO configuration
  const handleTestMinioConfig = async () => {
    if (!minioConfig.endpoint || !minioConfig.accessKey || !minioConfig.secretKey) {
      toast.error(t('settings.minio.errors.fillAllFields', 'Veuillez remplir tous les champs'));
      return;
    }

    setTestingMinio(true);
    try {
      await settingsAPI.testMinioConfig({
        endpoint: minioConfig.endpoint,
        port: minioConfig.port ? Number(minioConfig.port) : undefined,
        useSSL: !!minioConfig.useSSL,
        accessKey: minioConfig.accessKey,
        secretKey: minioConfig.secretKey,
        publicUrl: minioConfig.publicUrl || undefined
      });
      toast.success(t('settings.minio.testSuccess', 'Configuration MinIO valide ! ✅'));
    } catch (error) {
      console.error('Error testing MinIO config:', error);
      toast.error(error.response?.data?.error || t('settings.minio.testFailed', 'Configuration MinIO invalide'));
    } finally {
      setTestingMinio(false);
    }
  };

  // Save MinIO configuration
  const handleSaveMinioConfig = async () => {
    if (!minioConfig.endpoint || !minioConfig.accessKey || !minioConfig.secretKey) {
      toast.error(t('settings.minio.errors.fillAllFields', 'Veuillez remplir tous les champs'));
      return;
    }

    setSavingMinio(true);
    try {
      await settingsAPI.saveMinioConfig({
        endpoint: minioConfig.endpoint,
        port: minioConfig.port ? Number(minioConfig.port) : undefined,
        useSSL: !!minioConfig.useSSL,
        accessKey: minioConfig.accessKey,
        secretKey: minioConfig.secretKey,
        publicUrl: minioConfig.publicUrl || undefined
      });
      toast.success(t('settings.minio.saveSuccess', 'Configuration MinIO sauvegardée avec succès ! 🎉'));
      setShowMinioForm(false);
      setMinioConfig({
        endpoint: '',
        port: '9000',
        useSSL: false,
        accessKey: '',
        secretKey: '',
        publicUrl: ''
      });
      await fetchMinioConfigInfo();
    } catch (error) {
      console.error('Error saving MinIO config:', error);
      toast.error(error.response?.data?.error || t('settings.minio.saveError', 'Erreur lors de la sauvegarde de la configuration'));
    } finally {
      setSavingMinio(false);
    }
  };

  // Toggle MinIO configuration
  const handleToggleMinioConfig = async (enabled) => {
    try {
      await settingsAPI.toggleMinioConfig({ enabled });
      toast.success(enabled ? t('settings.minio.enabled', 'Configuration MinIO activée !') : t('settings.minio.disabled', 'Configuration MinIO désactivée !'));
      await fetchMinioConfigInfo();
    } catch (error) {
      console.error('Error toggling MinIO config:', error);
      toast.error(error.response?.data?.error || t('settings.minio.toggleError', 'Erreur lors du changement de statut'));
    }
  };

  // Delete MinIO configuration
  const handleDeleteMinioConfig = async () => {
    const confirmed = await confirm({
      title: t('settings.minio.deleteTitle', 'Supprimer la configuration MinIO'),
      message: t('settings.minio.deleteConfirm', 'Êtes-vous sûr de vouloir supprimer votre configuration MinIO personnalisée ?'),
      type: 'danger',
      confirmText: t('settings.actions.delete', 'Supprimer'),
      cancelText: t('settings.actions.cancel', 'Annuler')
    });
    if (!confirmed) return;

    try {
      await settingsAPI.deleteMinioConfig();
      toast.success(t('settings.minio.deleteSuccess', 'Configuration MinIO supprimée !'));
      await fetchMinioConfigInfo();
    } catch (error) {
      console.error('Error deleting MinIO config:', error);
      toast.error(error.response?.data?.error || t('settings.minio.deleteError', 'Erreur lors de la suppression'));
    }
  };

  // Get migration estimate
  const handleGetMigrationEstimate = async () => {
    try {
      const response = await settingsAPI.estimateMinioMigration();
      setMigrationEstimate(response.data?.estimation || response.data);
      setShowMigrationDetails(true);
    } catch (error) {
      console.error('Error getting migration estimate:', error);
      toast.error(error.response?.data?.error || t('settings.minio.migration.estimateError', 'Erreur lors de l\'estimation'));
    }
  };

  // Start migration to custom storage
  const handleStartMigrationToCustom = async () => {
    const confirmed = await confirm({
      title: t('settings.minio.migration.toCustomTitle', 'Migrer vers MinIO personnalisé'),
      message: t('settings.minio.migration.toCustomConfirm', 'Êtes-vous sûr de vouloir migrer vos données vers votre stockage MinIO personnalisé ? Cette opération peut prendre du temps.'),
      type: 'warning',
      confirmText: t('settings.minio.migration.start', 'Démarrer'),
      cancelText: t('settings.actions.cancel', 'Annuler')
    });
    if (!confirmed) return;

    try {
      await settingsAPI.migrateToCustomMinio();
      toast.success(t('settings.minio.migration.toCustomStarted', 'Migration démarrée en arrière-plan ! 🚀'));
      await fetchMinioConfigInfo();
    } catch (error) {
      console.error('Error starting migration to custom:', error);
      toast.error(error.response?.data?.error || t('settings.minio.migration.startError', 'Erreur lors du démarrage de la migration'));
    }
  };

  // Start migration from custom storage
  const handleStartMigrationFromCustom = async () => {
    const confirmed = await confirm({
      title: t('settings.minio.migration.fromCustomTitle', 'Revenir au stockage Candio'),
      message: t('settings.minio.migration.fromCustomConfirm', 'Êtes-vous sûr de vouloir migrer vos données vers le stockage Candio par défaut ? Cette opération peut prendre du temps.'),
      type: 'warning',
      confirmText: t('settings.minio.migration.start', 'Démarrer'),
      cancelText: t('settings.actions.cancel', 'Annuler')
    });
    if (!confirmed) return;

    try {
      await settingsAPI.migrateFromCustomMinio();
      toast.success(t('settings.minio.migration.fromCustomStarted', 'Migration de retour démarrée en arrière-plan ! 🔄'));
      await fetchMinioConfigInfo();
    } catch (error) {
      console.error('Error starting migration from custom:', error);
      toast.error(error.response?.data?.error || t('settings.minio.migration.startError', 'Erreur lors du démarrage de la migration'));
    }
  };

  // Fetch AI provider config info
  const fetchAiProviders = useCallback(async () => {
    try {
      setAiProviderLoading(true);
      const response = await settingsAPI.getAiProviders();
      setAiProviders(response.data?.providers || {
        openai: { configured: false, updatedAt: null, tokenPreview: null },
        anthropic: { configured: false, updatedAt: null, tokenPreview: null }
      });
    } catch (error) {
      console.error('Error fetching AI providers:', error);
    } finally {
      setAiProviderLoading(false);
    }
  }, []);

  const handleSaveAiProvider = async (provider) => {
    const token = aiProviderTokens[provider]?.trim();
    if (!token) {
      toast.error(t('settings.aiProviders.errors.required', 'Veuillez fournir une clé API valide'));
      return;
    }

    setAiProviderSaving((prev) => ({ ...prev, [provider]: true }));
    try {
      await settingsAPI.updateAiProvider(provider, { api_key: token });
      toast.success(t('settings.aiProviders.messages.saved', 'Clé API sauvegardée'));
      setAiProviderTokens((prev) => ({ ...prev, [provider]: '' }));
      await fetchAiProviders();
    } catch (error) {
      console.error('Error saving AI provider:', error);
      toast.error(error.response?.data?.error || t('settings.aiProviders.errors.saveFailed', 'Erreur lors de la sauvegarde'));
    } finally {
      setAiProviderSaving((prev) => ({ ...prev, [provider]: false }));
    }
  };

  const handleDeleteAiProvider = async (provider) => {
    const confirmed = await confirm({
      title: t('settings.aiProviders.confirmTitle', 'Supprimer la clé API'),
      message: t('settings.aiProviders.confirmDelete', 'Supprimer cette clé API ?'),
      type: 'danger',
      confirmText: t('settings.aiProviders.delete', 'Supprimer'),
      cancelText: t('common.cancel', 'Annuler')
    });
    if (!confirmed) return;
    try {
      await settingsAPI.deleteAiProvider(provider);
      toast.success(t('settings.aiProviders.messages.deleted', 'Clé API supprimée'));
      await fetchAiProviders();
    } catch (error) {
      console.error('Error deleting AI provider:', error);
      toast.error(error.response?.data?.error || t('settings.aiProviders.errors.deleteFailed', 'Erreur lors de la suppression'));
    }
  };

  // Fetch current jobs
  const fetchCurrentJobs = useCallback(async () => {
    try {
      setLoadingJobs(true);
      const response = await settingsAPI.getJobs({ status: ['processing', 'pending', 'retry'], limit: 20 });
      setCurrentJobs(response.data?.jobs || []);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoadingJobs(false);
    }
  }, []);

  const fetchUserData = useCallback(async () => {
    try {
      setLoading(true);
      const userResponse = await settingsAPI.getMe();

      const user = userResponse.data.user;
      const settings = userResponse.data.settings;

      setUserData(user);

      setProfileData({
        name: user.name || '',
        email: user.email || ''
      });

      setPreferencesData({
        language: settings.language || 'fr',
        cleanup_delay: settings.cleanup_delay || 30
      });

      if (user.role === 'admin') {
        await fetchIndustryOptions();
        await fetchNotificationsData();
        await fetchHrUsers();
        await fetchCompanyData();
        await fetchMinioConfigInfo();
        await fetchAiProviders();
        await fetchCurrentJobs();
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      toast.error(t('settings.errors.dataLoadFailed', 'Erreur lors du chargement des données'));
    } finally {
      setLoading(false);
    }
  }, [fetchAiProviders, fetchCompanyData, fetchCurrentJobs, fetchHrUsers, fetchIndustryOptions, fetchMinioConfigInfo, fetchNotificationsData, t]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  useEffect(() => {
    if (userData.role === 'admin') {
      fetchIndustryOptions();
    }
  }, [fetchIndustryOptions, userData.role]);

  // Get job status color
  const getJobStatusColor = (status) => {
    switch (status) {
      case 'processing': return 'text-blue-600';
      case 'completed': return 'text-green-600';
      case 'failed': return 'text-red-600';
      case 'retry': return 'text-yellow-600';
      default: return 'text-gray-600';
    }
  };

  // Get job status icon
  const getJobStatusIcon = (status) => {
    switch (status) {
      case 'processing': return <RefreshCw className="w-4 h-4 animate-spin" />;
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'failed': return <AlertCircle className="w-4 h-4" />;
      case 'retry': return <Clock className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  // Format job type for display
  const formatJobType = (jobType) => {
    switch (jobType) {
      case 'storage_migration': return t('settings.jobs.storageMigration', 'Migration stockage');
      case 'storage_cleanup': return t('settings.jobs.storageCleanup', 'Nettoyage stockage');
      case 'file_transfer': return t('settings.jobs.fileTransfer', 'Transfert de fichiers');
      default: return t('settings.jobs.unknown', 'Type inconnu');
    }
  };

  // Developer tab available for admins (pricing disabled)
  const hasEnterprisePlan = userData.role === 'admin';
  const selectedIndustryLabel = useMemo(
    () => industryOptions.find((option) => option.value === companyData.industry)?.label || '',
    [industryOptions, companyData.industry]
  );

  const tabs = useMemo(() => ([
    { id: 'account', label: t('settings.tabs.account', 'Compte'), icon: User },
    ...(userData.role === 'admin' ? [
      { id: 'company', label: t('settings.tabs.company', 'Entreprise'), icon: Globe },
      { id: 'notifications', label: t('settings.tabs.notifications', 'Notifications'), icon: Bell },
      { id: 'cleanup', label: t('settings.tabs.cleanup', 'Nettoyage automatique'), icon: Trash2 },
      ...(hasEnterprisePlan ? [
        { id: 'developer', label: t('settings.tabs.developer', 'Développeur'), icon: Code }
      ] : [])
    ] : [])
  ]), [hasEnterprisePlan, t, userData.role]);
  const tabIds = useMemo(() => tabs.map((tab) => tab.id), [tabs]);

  useEffect(() => {
    const legacyMap = {
      profile: 'account',
      password: 'account',
      preferences: 'account'
    };
    const rawTabParam = searchParams.get('tab');
    const tabParam = legacyMap[rawTabParam] || rawTabParam;
    if (tabParam && tabIds.includes(tabParam) && tabParam !== activeTab) {
      setActiveTab(tabParam);
    }
  }, [searchParams, tabIds, activeTab]);

  if (loading) {
    return (
      <div className="settings-modern p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-modern p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
          <Settings className="w-7 h-7 text-primary-600" />
          {t('settings.title', 'Paramètres')}
        </h1>
        <p className="text-sm text-gray-600">
          {t('settings.subtitle', 'Gérez votre profil, vos préférences et votre compte')}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar Navigation */}
        <div className="lg:col-span-1">
          <div className="card">
            <div className="p-0">
              <nav className="space-y-1">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => {
                        setActiveTab(tab.id);
                        const next = new URLSearchParams(searchParams);
                        next.set('tab', tab.id);
                        setSearchParams(next, { replace: true });
                      }}
                      className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                        activeTab === tab.id
                          ? 'bg-primary-100 text-primary-700 border-r-2 border-primary-500'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <Icon className="w-5 h-5 mr-3" />
                      {tab.label}
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Account Info Card */}
          <div className="card mt-6">
            <div className="card-content">
              <h3 className="text-sm font-medium text-gray-900 mb-3">{t('settings.accountInfo.title', 'Informations du compte')}</h3>
              <div className="space-y-2">
                <div className="flex items-center text-sm">
                  <Mail className="w-4 h-4 text-gray-400 mr-2" />
                  <span className="text-gray-600">{userData.email}</span>
                </div>
                <div className="flex items-center text-sm">
                  <Shield className="w-4 h-4 text-gray-400 mr-2" />
                  <span className="text-gray-600 capitalize">{userData.role}</span>
                </div>
                <div className="flex items-center text-sm">
                  <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                  <span className="text-gray-600">
                    {t('settings.accountInfo.registeredOn', 'Inscrit le {{date}}').replace('{{date}}', new Date(userData.created_at).toLocaleDateString('fr-FR'))}
                  </span>
                </div>
                <div className="flex items-center text-sm">
                  {userData.is_verified ? (
                    <>
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      <span className="text-green-600">{t('settings.accountInfo.verified', 'Compte vérifié')}</span>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-4 h-4 text-red-500 mr-2" />
                      <span className="text-red-600">{t('settings.accountInfo.unverified', 'Non vérifié')}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3">
          <div className="card">
            <div className="card-content">
              {/* Account Tab - merged profile/password/preferences */}
              {activeTab === 'account' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6 mt-6 first:mt-0"
                >
                  <div className="settings-section-panel">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('settings.profile.title', 'Informations du profil')}</h2>
                    <form onSubmit={handleProfileUpdate} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('settings.profile.fullName', 'Nom complet')}
                        </label>
                        <input
                          type="text"
                          value={profileData.name}
                          onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                          className="input"
                          placeholder={t('settings.profile.fullNamePlaceholder', 'Votre nom complet')}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('settings.profile.email', 'Adresse email')}
                        </label>
                        <input
                          type="email"
                          value={profileData.email}
                          onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                          className="input"
                          placeholder={t('settings.profile.emailPlaceholder', 'votre@email.com')}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          {t('settings.profile.emailChangeNote', 'Le changement d\'email nécessitera une nouvelle vérification')}
                        </p>
                      </div>

                      <div className="flex justify-end">
                        <button
                          type="submit"
                          disabled={saving}
                          className="btn-primary"
                        >
                          {saving ? (
                            <>
                              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                              {t('settings.actions.saving', 'Enregistrement...')}
                            </>
                          ) : (
                            <>
                              <Save className="w-4 h-4 mr-2" />
                              {t('settings.actions.save', 'Enregistrer')}
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </motion.div>
              )}

              {activeTab === 'account' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6 mt-6 first:mt-0"
                >
                  <div className="settings-section-panel">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('settings.password.title', 'Changer le mot de passe')}</h2>
                    <form onSubmit={handlePasswordChange} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('settings.password.current', 'Mot de passe actuel')}
                        </label>
                        <div className="relative">
                          <input
                            type={showPasswords.current ? 'text' : 'password'}
                            value={passwordData.currentPassword}
                            onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                            className="input pr-10"
                            placeholder={t('settings.password.currentPlaceholder', 'Votre mot de passe actuel')}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                            aria-label={showPasswords.current ? t('auth.password.hide') : t('auth.password.show')}
                          >
                            {showPasswords.current ? (
                              <EyeOff className="h-5 w-5 text-gray-400" />
                            ) : (
                              <Eye className="h-5 w-5 text-gray-400" />
                            )}
                          </button>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('settings.password.new', 'Nouveau mot de passe')}
                        </label>
                        <div className="relative">
                          <input
                            type={showPasswords.new ? 'text' : 'password'}
                            value={passwordData.newPassword}
                            onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                            className="input pr-10"
                            placeholder={t('settings.password.newPlaceholder', 'Votre nouveau mot de passe')}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                            aria-label={showPasswords.new ? t('auth.password.hide') : t('auth.password.show')}
                          >
                            {showPasswords.new ? (
                              <EyeOff className="h-5 w-5 text-gray-400" />
                            ) : (
                              <Eye className="h-5 w-5 text-gray-400" />
                            )}
                          </button>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {t('settings.password.minLengthNote', 'Minimum 8 caractères')}
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('settings.password.confirm', 'Confirmer le nouveau mot de passe')}
                        </label>
                        <div className="relative">
                          <input
                            type={showPasswords.confirm ? 'text' : 'password'}
                            value={passwordData.confirmPassword}
                            onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                            className="input pr-10"
                            placeholder={t('settings.password.confirmPlaceholder', 'Confirmez votre nouveau mot de passe')}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                            aria-label={showPasswords.confirm ? t('auth.password.hide') : t('auth.password.show')}
                          >
                            {showPasswords.confirm ? (
                              <EyeOff className="h-5 w-5 text-gray-400" />
                            ) : (
                              <Eye className="h-5 w-5 text-gray-400" />
                            )}
                          </button>
                        </div>
                      </div>

                      <div className="flex justify-end">
                        <button
                          type="submit"
                          disabled={saving}
                          className="btn-primary"
                        >
                          {saving ? (
                            <>
                              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                              {t('settings.password.changing', 'Changement...')}
                            </>
                          ) : (
                            <>
                              <Lock className="w-4 h-4 mr-2" />
                              {t('settings.password.changeButton', 'Changer le mot de passe')}
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </motion.div>
              )}

              {activeTab === 'account' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6 mt-6 first:mt-0"
                >
                  <div className="settings-section-panel">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('settings.preferences.title', 'Préférences')}</h2>
                    <form onSubmit={handlePreferencesUpdate} className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          <Globe className="w-4 h-4 inline mr-2" />
                          {t('settings.preferences.language', 'Langue de l\'interface')}
                        </label>
                        <select
                          value={preferencesData.language}
                          onChange={(e) => {
                            const newLanguage = e.target.value;
                            setPreferencesData({ ...preferencesData, language: newLanguage });
                            // Update language immediately for better UX
                            changeLanguage(newLanguage);
                          }}
                          className="input"
                        >
                          <option value="fr">{t('languages.french', 'Français')}</option>
                          <option value="en">{t('languages.english', 'English')}</option>
                          <option value="es">{t('languages.spanish', 'Español')}</option>
                        </select>
                      </div>



                      <div className="flex justify-end">
                        <button
                          type="submit"
                          disabled={saving}
                          className="btn-primary"
                        >
                          {saving ? (
                            <>
                              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                              {t('settings.actions.saving', 'Enregistrement...')}
                            </>
                          ) : (
                            <>
                              <Save className="w-4 h-4 mr-2" />
                              {t('settings.actions.save', 'Enregistrer')}
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </motion.div>
              )}

              {/* Company Tab (Admin only) */}
              {activeTab === 'company' && userData.role === 'admin' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6"
                >
                  <div className="settings-section-panel">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">
                      <Globe className="w-5 h-5 inline mr-2" />
                      {t('settings.company.title', 'Paramètres entreprise')}
                    </h2>
                    <p className="text-sm text-gray-600 mb-6">
                      {t('settings.company.subtitle', 'Configurez les informations de votre entreprise qui apparaîtront sur les pages publiques des offres d\'emploi.')}
                    </p>

                    <form onSubmit={handleCompanyUpdate} className="space-y-6">
                      {/* Nom de l'entreprise */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          <Globe className="w-4 h-4 inline mr-2" />
                          {t('settings.company.name', 'Nom de l\'entreprise')}
                        </label>
                        <input
                          type="text"
                          value={companyData.company_name}
                          onChange={(e) => setCompanyData({ ...companyData, company_name: e.target.value })}
                          className="input"
                          placeholder={t('settings.company.namePlaceholder', 'Nom de votre entreprise')}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          {t('settings.company.nameNote', 'Ce nom apparaîtra sur les pages publiques des offres d\'emploi')}
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          <Briefcase className="w-4 h-4 inline mr-2" />
                          {t('settings.company.industry', 'Secteur d\'activité')}
                        </label>
                        <select
                          value={companyData.industry || ''}
                          onChange={(e) => setCompanyData({ ...companyData, industry: e.target.value })}
                          className="input"
                        >
                          <option value="">{t('settings.company.selectIndustry', 'Sélectionner un secteur')}</option>
                          {industryOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Logo de l'entreprise */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('settings.company.logo.title', 'Logo de l\'entreprise')}
                        </label>
                        
                        {/* Prévisualisation du logo actuel */}
                        {companyData.company_logo_url && (
                          <div className="mb-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                            <p className="text-sm font-medium text-gray-700 mb-2">{t('settings.company.logo.current', 'Logo actuel :')}</p>
                            <div className="flex items-center space-x-4">
                              <img
                                src={companyData.company_logo_url}
                                alt={t('settings.company.logo.alt', 'Logo entreprise')}
                                className="h-16 w-16 object-contain border border-gray-200 rounded"
                                onError={handleCompanyLogoLoadError}
                              />
                              <button
                                type="button"
                                onClick={handleRemoveLogo}
                                className="px-3 py-1 text-sm text-red-600 hover:text-red-700 border border-red-300 rounded hover:border-red-400"
                              >
                                {t('settings.company.logo.remove', 'Supprimer')}
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Upload de logo */}
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                          <div className="space-y-2">
                            <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                              <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            <div>
                              <label htmlFor="logo-upload" className="cursor-pointer">
                                <span className="mt-2 block text-sm font-medium text-gray-900">
                                  {companyData.company_logo_url ? t('settings.company.logo.change', 'Changer le logo') : t('settings.company.logo.upload', 'Uploader un logo')}
                                </span>
                                <span className="mt-1 block text-xs text-gray-500">
                                  {t('settings.company.logo.formats', 'PNG, JPG, GIF, WebP jusqu\'à 5MB')}
                                </span>
                              </label>
                              <input
                                id="logo-upload"
                                type="file"
                                className="sr-only"
                                accept="image/*"
                                onChange={handleLogoFileChange}
                              />
                            </div>
                          </div>
                        </div>

                        {logoFile && (
                          <div className="mt-2 text-sm text-green-600">
                            {t('settings.company.logo.newSelected', '✓ Nouveau fichier sélectionné: {{name}}').replace('{{name}}', logoFile.name)}
                          </div>
                        )}

                        <p className="text-xs text-gray-500 mt-2">
                          {t('settings.company.logo.note', 'Le logo apparaîtra en en-tête des pages publiques des offres d\'emploi. Recommandé : format carré ou rectangle horizontal, fond transparent.')}
                        </p>
                      </div>

                      {/* Aperçu */}
                      {(companyData.company_name || companyData.company_logo_url || companyData.industry) && (
                        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                          <h3 className="text-sm font-medium text-gray-900 mb-3">
                            {t('settings.company.preview.title', 'Aperçu sur les pages publiques :')}
                          </h3>
                          <div className="bg-white p-4 rounded border">
                            <div className="flex items-center space-x-3">
                              {companyData.company_logo_url && (
                                <img
                                  src={companyData.company_logo_url}
                                  alt="Logo"
                                  className="h-8 w-8 object-contain"
                                  onError={handleCompanyLogoLoadError}
                                />
                              )}
                              <span className="text-lg font-semibold text-gray-900">
                                {companyData.company_name || t('settings.company.preview.placeholder', 'Nom de l\'entreprise')}
                              </span>
                            </div>
                            {selectedIndustryLabel && (
                              <p className="mt-2 text-sm text-gray-600">
                                {selectedIndustryLabel}
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="flex justify-end">
                        <button
                          type="submit"
                          disabled={saving}
                          className="btn-primary"
                        >
                          {saving ? (
                            <>
                              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                              {t('settings.actions.saving', 'Enregistrement...')}
                            </>
                          ) : (
                            <>
                              <Save className="w-4 h-4 mr-2" />
                              {t('settings.actions.save', 'Enregistrer')}
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </motion.div>
              )}

              {/* Notifications Tab (Admin only) */}
              {activeTab === 'notifications' && userData.role === 'admin' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6"
                >
                  <div className="settings-section-panel">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">
                      <Bell className="w-5 h-5 inline mr-2" />
                      {t('settings.notifications.mainTitle', 'Notifications')}
                    </h2>
                    <p className="text-sm text-gray-600 mb-6">
                      {t('settings.notifications.mainSubtitle', 'Configurez qui doit être notifié par email pour les différents événements de la plateforme.')}
                    </p>

                    <form onSubmit={handleNotificationsUpdate} className="space-y-6">
                      {/* Use the new NotificationsSection component */}
                      <NotificationsSection
                        t={t}
                        notificationsData={notificationsData}
                        setNotificationsData={setNotificationsData}
                        hrUsers={hrUsers}
                        toggleUserNotification={toggleUserNotification}
                        toggleAppointmentUser={toggleAppointmentUser}
                        toggleAnalysisUser={toggleAnalysisUser}
                      />

                      <div className="flex justify-end mt-6">
                        <button
                          type="submit"
                          disabled={saving}
                          className="btn-primary"
                        >
                          {saving ? (
                            <>
                              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                              {t('settings.actions.saving', 'Enregistrement...')}
                            </>
                          ) : (
                            <>
                              <Save className="w-4 h-4 mr-2" />
                              {t('settings.actions.save', 'Enregistrer')}
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </motion.div>
              )}

              {/* Cleanup Tab (Admin only) */}
              {activeTab === 'cleanup' && userData.role === 'admin' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6"
                >
                  <div className="settings-section-panel">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">
                      <Trash2 className="w-5 h-5 inline mr-2" />
                      {t('settings.cleanup.title', 'Nettoyage automatique')}
                    </h2>
                    <p className="text-sm text-gray-600 mb-6">
                      {t('settings.cleanup.subtitle', 'Configurez le nettoyage automatique des données pour optimiser l\'espace de stockage et maintenir les performances du système.')}
                    </p>
                    
                    <AutoCleanupSettings />
                  </div>
                </motion.div>
              )}

              {/* Developer Tab (Enterprise Admin only) */}
              {activeTab === 'developer' && userData.role === 'admin' && hasEnterprisePlan && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6"
                >
                  <div className="settings-section-panel">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Code className="w-5 h-5 mr-2 text-primary-600" />
                      {t('settings.developer.title', 'Configuration Développeur')}
                    </h2>

                    {/* AI Providers */}
                    <div className="settings-subsection-panel mb-6">
                      <div className="settings-subsection-header">
                        <h3 className="text-lg font-medium text-gray-900 flex items-center">
                          <Key className="w-5 h-5 mr-2 text-primary-600" />
                          {t('settings.aiProviders.title', 'Fournisseurs IA')}
                        </h3>
                        <p className="text-sm text-gray-600 mt-1">
                          {t('settings.aiProviders.subtitle', 'Configurez vos clés API pour activer les résumés et analyses IA')}
                        </p>
                      </div>

                      <div className="settings-subsection-content space-y-4">
                        {aiProviderLoading ? (
                          <p className="text-sm text-gray-500">{t('settings.aiProviders.loading', 'Chargement...')}</p>
                        ) : (
                          ['openai', 'anthropic'].map((provider) => {
                            const providerInfo = aiProviders[provider] || { configured: false, updatedAt: null, tokenPreview: null };
                            const providerLabel = provider === 'openai'
                              ? t('settings.aiProviders.openai.title', 'OpenAI')
                              : t('settings.aiProviders.anthropic.title', 'Anthropic');
                            const providerDesc = provider === 'openai'
                              ? t('settings.aiProviders.openai.description', 'Clé API pour les résumés et analyses IA.')
                              : t('settings.aiProviders.anthropic.description', 'Clé API pour les analyses avancées avec Claude.');

                            return (
                              <div key={provider} className="border border-gray-200 rounded-lg p-4">
                                <div className="flex items-start justify-between">
                                  <div>
                                    <h4 className="text-sm font-semibold text-gray-900">{providerLabel}</h4>
                                    <p className="text-xs text-gray-600">{providerDesc}</p>
                                  </div>
                                  <span className={`text-xs px-2 py-1 rounded-full ${
                                    providerInfo.configured ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                                  }`}>
                                    {providerInfo.configured ? t('common.active', 'Actif') : t('common.inactive', 'Inactif')}
                                  </span>
                                </div>

                                <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-3 items-center">
                                  <div className="relative lg:col-span-2">
                                    <input
                                      type={aiProviderVisibility[provider] ? 'text' : 'password'}
                                      value={aiProviderTokens[provider]}
                                      onChange={(e) => setAiProviderTokens((prev) => ({ ...prev, [provider]: e.target.value }))}
                                      placeholder={t('settings.aiProviders.apiKeyPlaceholder', 'sk-...')}
                                      className="input pr-10"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => setAiProviderVisibility((prev) => ({ ...prev, [provider]: !prev[provider] }))}
                                      className="absolute inset-y-0 right-2 flex items-center text-gray-400 hover:text-gray-600"
                                      aria-label={t('common.togglePassword', 'Afficher/masquer')}
                                    >
                                      {aiProviderVisibility[provider] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </button>
                                  </div>
                                  <div className="flex flex-wrap gap-2">
                                    <button
                                      type="button"
                                      onClick={() => handleSaveAiProvider(provider)}
                                      disabled={aiProviderSaving[provider]}
                                      className="btn-primary btn-sm"
                                    >
                                      {aiProviderSaving[provider] ? t('settings.actions.saving', 'Enregistrement...') : t('settings.aiProviders.save', 'Sauvegarder')}
                                    </button>
                                    {providerInfo.configured && (
                                      <button
                                        type="button"
                                        onClick={() => handleDeleteAiProvider(provider)}
                                        className="btn-secondary btn-sm text-red-600"
                                      >
                                        {t('settings.aiProviders.delete', 'Supprimer')}
                                      </button>
                                    )}
                                  </div>
                                </div>

                                <div className="mt-2 space-y-1">
                                  {providerInfo.tokenPreview && (
                                    <p className="text-xs text-gray-600">
                                      {t('settings.aiProviders.tokenPreview', 'Clé enregistrée : {{token}}')
                                        .replace('{{token}}', providerInfo.tokenPreview)}
                                    </p>
                                  )}
                                  {providerInfo.updatedAt && (
                                    <p className="text-xs text-gray-500">
                                      {t('settings.aiProviders.updatedAt', 'Mis à jour le {{date}}')
                                        .replace('{{date}}', new Date(providerInfo.updatedAt).toLocaleDateString('fr-FR'))}
                                    </p>
                                  )}
                                </div>
                              </div>
                            );
                          })
                        )}

                        <p className="text-xs text-gray-500">
                          {t('settings.aiProviders.note', 'Si aucune clé n’est définie, le système utilise les clés globales de Candio.')}
                        </p>
                      </div>
                    </div>
                    
                    {/* Warning Disclaimer */}
                    <div className="rounded-xl border border-orange-200 bg-orange-50 p-4 sm:p-5 mb-6">
                      <div className="flex items-start">
                        <AlertCircle className="w-5 h-5 text-orange-500 mr-3 mt-0.5 flex-shrink-0" />
                        <div>
                          <h3 className="text-sm font-medium text-orange-900 mb-2">{t('settings.minio.warning.title', '⚠️ Avertissement Important')}</h3>
                          <ul className="text-sm text-orange-800 space-y-1">
                            <li>{t('settings.minio.warning.technical', '• Cette fonctionnalité est réservée aux administrateurs techniques')}</li>
                            <li dangerouslySetInnerHTML={{ __html: t('settings.minio.warning.costs', '• L\'hébergement MinIO peut entraîner des <strong>coûts supplémentaires</strong> (serveurs, stockage, sauvegardes)') }} />
                            <li>{t('settings.minio.warning.ownership', '• Vous êtes responsable de l\'infrastructure MinIO et de sa sécurité')}</li>
                            <li>{t('settings.minio.warning.misconfiguration', '• Une mauvaise configuration peut rendre vos fichiers inaccessibles')}</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    {/* MinIO Custom Storage Section */}
                    <div className="settings-subsection-panel">
                      <div className="settings-subsection-header">
                        <h3 className="text-lg font-medium text-gray-900 flex items-center">
                          <Server className="w-5 h-5 mr-2 text-blue-600" />
                          {t('settings.minio.title', 'Stockage MinIO personnalisé')}
                        </h3>
                        <p className="text-sm text-gray-600 mt-1">
                          {t('settings.minio.subtitle', 'Configurez votre propre serveur MinIO pour un contrôle total de vos données')}
                        </p>
                      </div>

                      <div className="settings-subsection-content">
                        {/* Current Status */}
                        <div className="mb-6">
                          <h4 className="text-sm font-medium text-gray-900 mb-3">
                            {t('settings.minio.status.title', 'Statut actuel')}
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="flex items-center">
                              <div className={`w-3 h-3 rounded-full mr-2 ${
                                minioConfigInfo.hasCustomConfig ? 'bg-green-500' : 'bg-gray-300'
                              }`}></div>
                              <span className="text-sm text-gray-600">
                                {minioConfigInfo.hasCustomConfig ? t('settings.minio.status.hasConfig', 'Configuration sauvegardée') : t('settings.minio.status.noConfig', 'Pas de configuration')}
                              </span>
                            </div>
                            <div className="flex items-center">
                              <div className={`w-3 h-3 rounded-full mr-2 ${
                                minioConfigInfo.isEnabled ? 'bg-green-500' : 'bg-yellow-500'
                              }`}></div>
                              <span className="text-sm text-gray-600">
                                {minioConfigInfo.isEnabled ? t('settings.minio.status.enabled', 'Stockage actif') : t('settings.minio.status.disabled', 'Stockage Candio par défaut')}
                              </span>
                            </div>
                            <div className="flex items-center">
                              <Database className="w-4 h-4 mr-2 text-gray-400" />
                              <span className="text-sm text-gray-600">
                                {t('settings.minio.status.migration', 'Migration: {{status}}').replace('{{status}}', minioConfigInfo.migrationStatus || t('settings.minio.migration.none', 'aucune'))}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Configuration Actions */}
                        <div className="space-y-4">
                          {!minioConfigInfo.hasCustomConfig ? (
                            /* No Configuration - Show Form */
                            <div>
                              {!showMinioForm ? (
                                <button
                                  onClick={() => setShowMinioForm(true)}
                                  className="btn-primary flex items-center"
                                >
                                  <Key className="w-4 h-4 mr-2" />
                                  {t('settings.minio.config.configure', 'Configurer le stockage MinIO')}
                                </button>
                              ) : (
                                <div className="rounded-xl border border-gray-200 bg-gray-50/70 p-4 sm:p-5 space-y-4">
                                  <div className="flex items-center justify-between">
                                    <h5 className="text-sm font-medium text-gray-900">
                                      {t('settings.minio.config.title', 'Configuration MinIO')}
                                    </h5>
                                    <button
                                      onClick={() => {
                                        setShowMinioForm(false);
                                        setMinioConfig({
                                          endpoint: '',
                                          port: '9000',
                                          useSSL: false,
                                          accessKey: '',
                                          secretKey: '',
                                          publicUrl: ''
                                        });
                                      }}
                                      className="text-gray-400 hover:text-gray-600"
                                    >
                                      ×
                                    </button>
                                  </div>
                                  
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('settings.minio.config.endpoint', 'Endpoint')}
                                      </label>
                                      <input
                                        type="text"
                                        value={minioConfig.endpoint}
                                        onChange={(e) => setMinioConfig({ ...minioConfig, endpoint: e.target.value })}
                                        className="input"
                                        placeholder={t('settings.minio.config.endpointPlaceholder', 'minio.votre-domaine.com')}
                                      />
                                    </div>

                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('settings.minio.config.port', 'Port')}
                                      </label>
                                      <input
                                        type="number"
                                        value={minioConfig.port}
                                        onChange={(e) => setMinioConfig({ ...minioConfig, port: e.target.value })}
                                        className="input"
                                        min="1"
                                        max="65535"
                                      />
                                    </div>

                                    <div className="flex items-center gap-2">
                                      <input
                                        id="minio-use-ssl"
                                        type="checkbox"
                                        checked={!!minioConfig.useSSL}
                                        onChange={(e) => setMinioConfig({ ...minioConfig, useSSL: e.target.checked })}
                                        className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                                      />
                                      <label htmlFor="minio-use-ssl" className="text-sm text-gray-700">
                                        {t('settings.minio.config.useSSL', 'Utiliser SSL')}
                                      </label>
                                    </div>

                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('settings.minio.config.accessKey', 'Access key')}
                                      </label>
                                      <input
                                        type="text"
                                        value={minioConfig.accessKey}
                                        onChange={(e) => setMinioConfig({ ...minioConfig, accessKey: e.target.value })}
                                        className="input"
                                      />
                                    </div>

                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('settings.minio.config.secretKey', 'Secret key')}
                                      </label>
                                      <input
                                        type="password"
                                        value={minioConfig.secretKey}
                                        onChange={(e) => setMinioConfig({ ...minioConfig, secretKey: e.target.value })}
                                        className="input"
                                      />
                                    </div>

                                    <div className="md:col-span-2">
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('settings.minio.config.publicUrl', 'URL publique (optionnelle)')}
                                      </label>
                                      <input
                                        type="url"
                                        value={minioConfig.publicUrl}
                                        onChange={(e) => setMinioConfig({ ...minioConfig, publicUrl: e.target.value })}
                                        className="input"
                                      />
                                    </div>
                                  </div>

                                  <div className="flex space-x-3">
                                    <button
                                      onClick={handleTestMinioConfig}
                                      disabled={testingMinio}
                                      className="btn-secondary flex items-center"
                                    >
                                      {testingMinio ? (
                                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                      ) : (
                                        <CheckCircle className="w-4 h-4 mr-2" />
                                      )}
                                      {t('settings.minio.config.testConnection', 'Tester la connexion')}
                                    </button>
                                    
                                    <button
                                      onClick={handleSaveMinioConfig}
                                      disabled={savingMinio}
                                      className="btn-primary flex items-center"
                                    >
                                      {savingMinio ? (
                                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                      ) : (
                                        <Save className="w-4 h-4 mr-2" />
                                      )}
                                      {t('settings.minio.config.save', 'Sauvegarder')}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            /* Has Configuration - Show Management Options */
                            <div className="space-y-4">
                              <div className="rounded-xl border border-green-200 bg-green-50 p-4 sm:p-5">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <h5 className="text-sm font-medium text-green-900">{t('settings.minio.config.configSaved', 'Configuration sauvegardée')}</h5>
                                    <p className="text-sm text-green-700">
                                      {t('settings.minio.config.configuredOn', 'Configuré le')} {minioConfigInfo.configuredAt ? 
                                        new Date(minioConfigInfo.configuredAt).toLocaleDateString('fr-FR') : t('settings.minio.config.dateUnknown', 'Date inconnue')}
                                    </p>
                                  </div>
                                  <div className="flex space-x-2">
                                    <button
                                      onClick={() => handleToggleMinioConfig(!minioConfigInfo.isEnabled)}
                                      className={`px-4 py-2 text-sm font-medium rounded-lg flex items-center ${
                                        minioConfigInfo.isEnabled 
                                          ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                                          : 'bg-green-100 text-green-800 hover:bg-green-200'
                                      }`}
                                    >
                                      {minioConfigInfo.isEnabled ? (
                                        <>
                                          <Download className="w-4 h-4 mr-2" />
                                          {t('settings.minio.config.disable', 'Désactiver')}
                                        </>
                                      ) : (
                                        <>
                                          <Upload className="w-4 h-4 mr-2" />
                                          {t('settings.minio.config.enable', 'Activer')}
                                        </>
                                      )}
                                    </button>
                                    
                                    <button
                                      onClick={handleDeleteMinioConfig}
                                      className="px-4 py-2 text-sm font-medium bg-red-100 text-red-800 hover:bg-red-200 rounded-lg flex items-center"
                                    >
                                      <Trash2 className="w-4 h-4 mr-2" />
                                      {t('settings.minio.config.delete', 'Supprimer')}
                                    </button>
                                  </div>
                                </div>
                              </div>

                              {/* Migration Status if applicable */}
                              {minioConfigInfo.migrationStatus && minioConfigInfo.migrationStatus !== 'none' && (
                                <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 sm:p-5">
                                  <h5 className="text-sm font-medium text-blue-900 mb-2">{t('settings.minio.migration.inProgress', 'Migration en cours')}</h5>
                                  <div className="flex items-center">
                                    <div className="flex-1 bg-blue-200 rounded-full h-2 mr-3">
                                      <div className="bg-blue-600 h-2 rounded-full" style={{ width: '60%' }}></div>
                                    </div>
                                    <span className="text-sm text-blue-700">
                                      {t('settings.minio.migration.status', 'Statut:')} {minioConfigInfo.migrationStatus}
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Migration Section */}
                        {minioConfigInfo.hasCustomConfig && (
                          <div className="mt-6 pt-6 border-t border-gray-200">
                            <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                              <Database className="w-4 h-4 mr-2" />
                              {t('settings.minio.migration.title', 'Migration des données')}
                            </h4>
                            
                            <div className="space-y-4">
                              <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 sm:p-5">
                                <p className="text-sm text-blue-800 mb-3">
                                  {t('settings.minio.migration.description', 'Vous pouvez migrer vos données existantes entre le stockage Candio par défaut et votre stockage MinIO personnalisé.')}
                                </p>
                                
                                <div className="flex flex-wrap gap-3">
                                  <button
                                    onClick={handleGetMigrationEstimate}
                                    className="btn-secondary flex items-center text-sm"
                                  >
                                    <BarChart3 className="w-4 h-4 mr-2" />
                                    {t('settings.minio.migration.estimate', 'Estimer la migration')}
                                  </button>
                                  
                                  {!minioConfigInfo.isEnabled ? (
                                    <button
                                      onClick={handleStartMigrationToCustom}
                                      className="btn-primary flex items-center text-sm"
                                      disabled={minioConfigInfo.migrationStatus === 'in_progress'}
                                    >
                                      <Upload className="w-4 h-4 mr-2" />
                                      {t('settings.minio.migration.toCustom', 'Migrer vers mon stockage')}
                                    </button>
                                  ) : (
                                    <button
                                      onClick={handleStartMigrationFromCustom}
                                      className="btn-secondary flex items-center text-sm"
                                      disabled={minioConfigInfo.migrationStatus === 'reverting'}
                                    >
                                      <Download className="w-4 h-4 mr-2" />
                                      {t('settings.minio.migration.fromCustom', 'Revenir au stockage Candio')}
                                    </button>
                                  )}
                                </div>
                              </div>

                              {/* Migration Estimate Modal */}
                              {showMigrationDetails && migrationEstimate && (
                                <div className="bg-white border border-gray-300 p-4 rounded-lg shadow-sm">
                                  <div className="flex items-center justify-between mb-3">
                                    <h5 className="text-sm font-medium text-gray-900">{t('settings.minio.migration.estimateTitle', 'Estimation de migration')}</h5>
                                    <button
                                      onClick={() => setShowMigrationDetails(false)}
                                      className="text-gray-400 hover:text-gray-600"
                                      aria-label={t('common.close')}
                                    >
                                      ×
                                    </button>
                                  </div>
                                  
                                  <div className="grid grid-cols-2 gap-4 mb-4">
                                    <div className="text-center">
                                      <div className="text-2xl font-bold text-primary-600">{migrationEstimate.totalFiles}</div>
                                      <div className="text-sm text-gray-600">{t('settings.minio.migration.totalFiles', 'Fichiers à migrer')}</div>
                                    </div>
                                    <div className="text-center">
                                      <div className="text-2xl font-bold text-primary-600">{migrationEstimate.totalSizeFormatted}</div>
                                      <div className="text-sm text-gray-600">{t('settings.minio.migration.totalSize', 'Taille totale')}</div>
                                    </div>
                                  </div>
                                  
                                  <div className="space-y-2">
                                    {Object.entries(migrationEstimate.details).map(([folder, details]) => (
                                      <div key={folder} className="flex justify-between text-sm">
                                        <span className="capitalize">{folder}:</span>
                                        <span>{details.files} {t('settings.minio.migration.files', 'fichiers')} ({details.sizeFormatted})</span>
                                      </div>
                                    ))}
                                  </div>
                                  
                                  <div className="mt-3 pt-3 border-t border-gray-200">
                                    <p className="text-sm text-gray-600">
                                      <Clock className="w-4 h-4 inline mr-1" />
                                      {t('settings.minio.migration.duration', 'Durée estimée: {{duration}}').replace('{{duration}}', migrationEstimate.estimatedDuration)}
                                    </p>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Background Jobs Monitoring */}
                        <div className="mt-6 pt-6 border-t border-gray-200">
                          <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                            <RefreshCw className="w-4 h-4 mr-2" />
                            {t('settings.jobs.title', 'Jobs en arrière-plan')}
                            <button
                              onClick={fetchCurrentJobs}
                              className="ml-auto text-blue-600 hover:text-blue-800 text-sm"
                            >
                              {t('settings.jobs.refresh', 'Actualiser')}
                            </button>
                          </h4>
                          
                          {loadingJobs ? (
                            <div className="flex items-center justify-center py-4">
                              <RefreshCw className="w-5 h-5 animate-spin text-gray-400" />
                              <span className="ml-2 text-sm text-gray-600">{t('settings.jobs.loading', 'Chargement...')}</span>
                            </div>
                          ) : currentJobs.length > 0 ? (
                            <div className="space-y-3">
                              {currentJobs.map((job) => (
                                <div key={job.id} className="bg-gray-50 border border-gray-200 p-3 rounded-lg">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center">
                                      <span className={`${getJobStatusColor(job.status)} mr-2`}>
                                        {getJobStatusIcon(job.status)}
                                      </span>
                                      <span className="text-sm font-medium text-gray-900">
                                        {formatJobType(job.job_type)}
                                      </span>
                                    </div>
                                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                                      job.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                                      job.status === 'completed' ? 'bg-green-100 text-green-800' :
                                      job.status === 'failed' ? 'bg-red-100 text-red-800' :
                                      job.status === 'retry' ? 'bg-yellow-100 text-yellow-800' :
                                      'bg-gray-100 text-gray-800'
                                    }`}>
                                      {job.status}
                                    </span>
                                  </div>
                                  
                                  {job.progress > 0 && (
                                    <div className="mb-2">
                                      <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                                        <span>{t('settings.jobs.progress', 'Progression')}</span>
                                        <span>{job.progress}%</span>
                                      </div>
                                      <div className="w-full bg-gray-200 rounded-full h-2">
                                        <div 
                                          className="bg-blue-600 h-2 rounded-full transition-all"
                                          style={{ width: `${job.progress}%` }}
                                        ></div>
                                      </div>
                                      {job.progress_message && (
                                        <p className="text-xs text-gray-600 mt-1">{job.progress_message}</p>
                                      )}
                                    </div>
                                  )}
                                  
                                  <div className="flex items-center justify-between text-xs text-gray-500">
                                    <span>{t('settings.jobs.created', 'Créé le {{date}} à {{time}}')
                                      .replace('{{date}}', new Date(job.created_at).toLocaleDateString('fr-FR'))
                                      .replace('{{time}}', new Date(job.created_at).toLocaleTimeString('fr-FR'))}</span>
                                    {job.attempts > 0 && (
                                      <span>{t('settings.jobs.attempts', 'Tentative {{current}}/{{max}}')
                                        .replace('{{current}}', job.attempts)
                                        .replace('{{max}}', job.max_attempts)}</span>
                                    )}
                                  </div>
                                  
                                  {job.error_message && (
                                    <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                                      <strong>{t('settings.jobs.error', 'Erreur:')}</strong> {job.error_message}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-4">
                              <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                              <p className="text-sm text-gray-600">{t('settings.jobs.noJobs', 'Aucun job en cours')}</p>
                            </div>
                          )}
                        </div>

                        {/* Additional Info */}
                        <div className="mt-6 pt-6 border-t border-gray-200">
                          <h4 className="text-sm font-medium text-gray-900 mb-3">{t('settings.developer.techInfo.title', 'Informations techniques')}</h4>
                          <div className="text-sm text-gray-600 space-y-2">
                            <p dangerouslySetInnerHTML={{ __html: t('settings.developer.techInfo.containers', '• Vos fichiers seront stockés dans des conteneurs privés nommés admin-{{id}}')
                              .replace('{{id}}', userData.id)
                              .replace(`admin-${userData.id}`, `<code class="bg-gray-100 px-1 rounded">admin-${userData.id}</code>`) }} />
                            <p dangerouslySetInnerHTML={{ __html: t('settings.developer.techInfo.structure', '• La structure des dossiers suit le format: cvs/, logos/, photos/').replace('cvs/', '<code class="bg-gray-100 px-1 rounded">cvs/</code>').replace('logos/', '<code class="bg-gray-100 px-1 rounded">logos/</code>').replace('photos/', '<code class="bg-gray-100 px-1 rounded">photos/</code>') }} />
                            <p>{t('settings.developer.techInfo.security', '• Les accès sont sécurisés par des tokens SAS temporaires')}</p>
                            <p>{t('settings.developer.techInfo.migration', '• La migration peut prendre du temps selon le volume de données')}</p>
                            <p>{t('settings.developer.techInfo.background', '• Les migrations s\'exécutent en arrière-plan pour ne pas bloquer l\'application')}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </div>
      <ConfirmationModal {...confirmProps} />
    </div>
  );
};

export default SettingsPage;
